import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import type { Domain, DomainCheck, ServiceDetail } from '../lib/types'
import { Button, ConfirmDialog, CopyButton } from './ui'

export default function DomainsTab({ service, onChange }: {
  service: ServiceDetail
  onChange: () => void
}) {
  const eligible = service.custom_domains_allowed === true
  const upgradePath = service.project_id ? `/projects/${service.project_id}` : '/billing'
  const [hostname, setHostname] = useState('')
  const [domains, setDomains] = useState(service.domains)
  const [busy, setBusy] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [checks, setChecks] = useState<Record<string, DomainCheck>>({})
  const [removing, setRemoving] = useState<Domain | null>(null)
  const [notice, setNotice] = useState('')
  const [recordKinds, setRecordKinds] = useState<Record<string, 'address' | 'cname'>>({})
  useEffect(() => { setDomains(service.domains) }, [service.domains])
  const checkKey = domains.filter(domain => !domain.is_default)
    .map(domain => `${domain.id}:${domain.verified}`).join('|')
  useEffect(() => {
    let stopped = false
    let timer: ReturnType<typeof setTimeout> | undefined
    let rounds = 0
    const ids = checkKey.split('|').filter(Boolean).map(item => item.split(':')[0])
    async function refresh() {
      for (const id of ids) {
        if (stopped) return
        try {
          const result = await api.checkDomain(service.id, id)
          if (!stopped) {
            setChecks(current => ({ ...current, [id]: result }))
            setErrors(current => ({ ...current, [id]: '' }))
          }
        } catch (error) {
          if (!stopped) {
            setChecks(current => { const next = { ...current }; delete next[id]; return next })
            setErrors(current => ({ ...current, [id]: error instanceof Error ? error.message : 'Status check failed.' }))
          }
        }
      }
      rounds += 1
      if (!stopped && rounds < 5 && ids.length) timer = setTimeout(() => void refresh(), 30000)
    }
    void refresh()
    return () => { stopped = true; if (timer) clearTimeout(timer) }
  }, [service.id, checkKey])

  async function run(key: string, action: () => Promise<void>) {
    setBusy(key)
    setErrors(current => ({ ...current, [key]: '' }))
    setNotice('')
    try { await action() }
    catch (error) {
      setErrors(current => ({
        ...current,
        [key]: error instanceof Error ? error.message : 'Something went wrong. Please try again.',
      }))
    } finally { setBusy(null) }
  }

  async function add() {
    if (!eligible || !hostname.trim() || busy) return
    await run('add', async () => {
      const domain = await api.addDomain(service.id, hostname.trim())
      setDomains(current => [...current.filter(item => item.id !== domain.id), domain])
      setHostname('')
      setNotice(`${domain.hostname} is attached to this service. Its DNS records and status are shown below.`)
      onChange()
    })
  }

  async function connect(domain: Domain) {
    await run(domain.id, async () => {
      const result = await api.checkDomain(service.id, domain.id)
      setChecks(current => ({ ...current, [domain.id]: result }))
      if (domain.verified || !eligible) return
      if (!result.ownership_verified || !result.routing_verified) return
      const updated = await api.verifyDomain(service.id, domain.id)
      setDomains(current => current.map(item => item.id === domain.id ? updated : item))
      setNotice(`${domain.hostname}: routing applied. Checking its HTTPS certificate as it becomes available.`)
      onChange()
    })
  }

  const custom = domains.filter(domain => !domain.is_default)
  const defaults = domains.filter(domain => domain.is_default)
  return <div className="stack domain-workspace">
    <section className="card card-pad-lg stack">
      <div><p className="platform-eyebrow">DOMAINS</p><h2>Connect your domain</h2>
        <p className="muted">Give <strong>{service.name}</strong> a custom web address.</p></div>
      {!eligible && <div className="alert info" role="status">
        <strong>Custom domains require paid Starter or higher.</strong>
        <p>Upgrade this application's plan to connect your own domain. Your included TalariCloud address stays available. Frontend and backend share the same application plan.</p>
        <Link className="btn primary" to={upgradePath}>View plans &amp; upgrade</Link>
      </div>}
      {eligible && <p className="tiny muted">Included with your {service.application_plan} application plan. No separate domain connection fee.</p>}
      <form className="stack" onSubmit={event => { event.preventDefault(); void add() }}>
        <label htmlFor="custom-hostname">Domain name</label>
        <div className="domain-add-row">
          <input id="custom-hostname" type="text" autoComplete="off" autoCapitalize="none"
            spellCheck={false} placeholder="www.example.com" value={hostname}
            disabled={!eligible || busy === 'add'} aria-invalid={!!errors.add}
            aria-describedby={errors.add ? 'domain-add-error domain-add-hint' : 'domain-add-hint'}
            onChange={event => setHostname(event.target.value)} />
          <Button type="submit" variant="primary" disabled={!eligible || !hostname.trim() || !!busy}
            loading={busy === 'add'}>Add domain</Button>
        </div>
        <p id="domain-add-hint" className="tiny muted">You can connect multiple hostnames to this component. Add example.com and www.example.com separately to use both addresses. Each hostname has its own DNS instructions.</p>
        {errors.add && <div id="domain-add-error" className="alert error" role="alert"><strong>Could not add domain.</strong> {errors.add}</div>}
      </form>
    </section>
    {notice && <p className="platform-notice" role="status">{notice}</p>}
    <div className="platform-section-head"><h2>Custom domains</h2><span className="tiny muted">{custom.length} connected or pending</span></div>
    {custom.length === 0 && <div className="card card-pad-lg"><h3>No custom domain yet</h3><p className="muted">Add your domain above. Then we’ll show the exact records to copy into your DNS provider.</p></div>}
    {custom.map(domain => {
      const check = checks[domain.id]
      const recordKind = recordKinds[domain.id] ?? (domain.hostname.split('.').length === 2 ? 'address' : 'cname')
      const dnsChanged = check && (!check.ownership_verified || !check.routing_verified)
      const secure = !!check && !dnsChanged && domain.verified && check.https_status === 'valid'
      const status = !check ? 'Status not checked' : dnsChanged ? 'DNS needs attention' : secure ? 'Connected · HTTPS verified' : domain.verified ? 'HTTPS needs attention' : 'Ready to activate'
      return <section className="card card-pad-lg stack" key={domain.id} aria-label={`Domain ${domain.hostname}`}>
        <div className="domain-card-heading"><h3 className="mono">{domain.hostname}</h3>
          <span className={`pill ${secure ? 'ok' : 'warn'}`}>{status}</span></div>
        <div className="domain-health-grid" role="status">
          <div><span className="tiny muted">Ownership</span><strong>{!check ? 'Checking…' : check.ownership_verified ? 'Verified' : 'TXT record required'}</strong></div>
          <div><span className="tiny muted">DNS routing</span><strong>{!check ? 'Checking…' : check.routing_verified ? 'Correct' : 'Needs attention'}</strong></div>
          <div><span className="tiny muted">HTTPS certificate</span><strong>{!check ? 'Checking…' : check.https_status === 'valid' ? 'Trusted and valid' : check.https_status === 'error' ? 'Certificate not valid' : check.https_status === 'unreachable' ? 'Connection unavailable' : 'Not checked'}</strong></div>
        </div>
        {check?.https_detail && <p className="tiny muted">{check.https_detail}</p>}
        {check?.https_status === 'valid' && <div className="domain-card-heading tiny">
          <span>Issued by <strong>{check.certificate_issuer || 'Trusted certificate authority'}</strong></span>
          {check.certificate_expires_at && <span>Expires <strong>{new Date(check.certificate_expires_at).toLocaleDateString()}</strong></span>}
        </div>}
        <p className="muted">In your DNS provider, add these records. Copy the values exactly.</p>
        <label htmlFor={`routing-${domain.id}`}>Routing record type</label>
        <select id={`routing-${domain.id}`} value={recordKind} onChange={event => setRecordKinds(current => ({ ...current, [domain.id]: event.target.value as 'address' | 'cname' }))}>
          <option value="address">A / AAAA — root domain or IP address</option>
          <option value="cname">CNAME — subdomain or supported flattening</option>
        </select>
        <p className="tiny muted">Use one routing option for this hostname. A / AAAA works when your provider does not support a CNAME at the root.</p>
        <div className="domain-records-scroll"><table className="domain-records">
          <caption className="sr-only">DNS records for {domain.hostname}</caption>
          <thead><tr><th scope="col">Type</th><th scope="col">Name</th><th scope="col">Value / target</th></tr></thead>
          <tbody>
            {domain.verification_record && domain.verification_value && <tr>
              <td><strong>TXT</strong><span className="tiny muted">Ownership</span></td>
              <td><code>{domain.verification_record}</code><CopyButton value={domain.verification_record} label="Copy name" /></td>
              <td><code>{domain.verification_value}</code><CopyButton value={domain.verification_value} label="Copy value" /></td>
            </tr>}
            {recordKind === 'address' && check?.expected_addresses.map(address => <tr key={address}>
              <td><strong>{address.includes(':') ? 'AAAA' : 'A'}</strong><span className="tiny muted">Routing</span></td>
              <td><code>{domain.hostname}</code><CopyButton value={domain.hostname} label="Copy name" /></td>
              <td><code>{address}</code><CopyButton value={address} label="Copy address" /></td>
            </tr>)}
            {recordKind === 'cname' && domain.dns_target && <tr>
              <td><strong>CNAME</strong><span className="tiny muted">Routing</span></td>
              <td><code>{domain.hostname}</code><CopyButton value={domain.hostname} label="Copy name" /></td>
              <td><code>{domain.dns_target}</code><CopyButton value={domain.dns_target} label="Copy target" /></td>
            </tr>}
          </tbody>
        </table></div>
        {recordKind === 'address' && !check?.expected_addresses.length && <p className="alert info">Waiting for the ingress addresses. Refresh status before adding A or AAAA records.</p>}
        {!domain.dns_target && <p className="alert error">Routing target is unavailable. Check again before changing DNS.</p>}
        <details><summary>Cloudflare, root domains, and DNS help</summary>
          <div className="stack tiny" style={{ marginTop: 12 }}>
            <p><strong>Cloudflare:</strong> open DNS → Records. Use DNS only (grey cloud) during verification.</p>
            <p><strong>Root domain, such as example.com:</strong> select A / AAAA above and use @ as the name when your provider appends the domain. Use CNAME only if your provider supports flattening at the root.</p>
            <p><strong>Record names:</strong> the table uses full hostnames. Some providers add your domain automatically; enter @ for the root or www for www.example.com when required by your provider.</p>
            <p>Keep your email records. DNS updates can take time to appear; check again after saving your records.</p>
          </div>
        </details>
        {check && <div className="domain-check-result" role="status">
          <div className="domain-card-heading"><span>Ownership: <strong>{check.ownership_verified ? 'Verified' : 'TXT record missing or incorrect'}</strong></span>
            <span>Routing: <strong>{check.routing_verified ? 'Correct' : 'Needs attention'}</strong></span></div>
          <p>{check.next_step}</p>
          <details><summary>DNS details</summary><p className="tiny">Expected addresses: {check.expected_addresses.join(', ') || 'Unavailable'}</p>
            <p className="tiny">Observed addresses: {check.observed_addresses.join(', ') || 'Not resolved'}</p></details>
        </div>}
        {errors[domain.id] && <p className="alert error" role="alert">{errors[domain.id]}</p>}
        <div className="domain-actions">
          <Button variant="primary" disabled={!!busy || (!eligible && !domain.verified)} loading={busy === domain.id}
            onClick={() => void connect(domain)}>{domain.verified ? 'Refresh status' : 'Check & activate'}</Button>
          {domain.verified && <a href={`https://${domain.hostname}`} target="_blank" rel="noreferrer">Open site ↗</a>}
          <Button size="sm" variant="danger" disabled={!!busy} onClick={() => setRemoving(domain)}>Remove domain</Button>
        </div>
        <p className="tiny muted">{check?.checked_at ? `Last checked ${new Date(check.checked_at).toLocaleString()}. ` : ''}Status refreshes for the first two minutes on this page. Certificate checks do not test application functionality.</p>
      </section>
    })}
    {defaults.length > 0 && <section className="card card-pad-lg stack"><h3>TalariCloud addresses</h3>
      <p className="tiny muted">These addresses are assigned automatically and cannot be renamed here.</p>
      {defaults.map(domain => <div className="domain-card-heading" key={domain.id}><span className="mono">{domain.hostname}</span><CopyButton value={`https://${domain.hostname}`} label="Copy URL" /></div>)}
    </section>}
    <ConfirmDialog open={!!removing} title="Remove this domain?" confirmLabel="Remove domain" destructive
      busy={busy === 'remove'} body={<>{removing?.hostname || 'This domain'} will no longer route to {service.name}. Your application and DNS provider records will remain.
        {errors.remove && <span className="alert error" role="alert">{errors.remove}</span>}</>}
      onCancel={() => setRemoving(null)} onConfirm={() => {
        if (!removing) return
        const domain = removing
        void run('remove', async () => {
          await api.removeDomain(service.id, domain.id)
          setDomains(current => current.filter(item => item.id !== domain.id))
          setRemoving(null)
          setNotice(`${domain.hostname} removed.`)
          onChange()
        })
      }} />
  </div>
}
